import { EMPTY_JSON_CONTENT, JSONContentSchema } from '@littlebird/sdk';
import { Priority } from 'kbar';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { ModalsManager } from '@/components/modals/modalsManager';
import { getText } from '@/components/ui/editor/utils';
import { getEnvVar } from '@/env';
import { createAction, Section } from '@/features/cmd-bar/actions';
import { type CommandBarContext } from '@/features/cmd-bar/actions/types';
import { ShareMeetingModal } from '@/features/meetings/meeting-share.modals';
import { userStore } from '@/stores/user.store';
import { AppCapability, isCapable } from '@/utils/capabilities';
import { navigate } from '@/utils/navigation-service';

import { meetingApis } from './meetings.query';
import { getMeeting as getMeetingById, meetingStore } from './meetings.state';
import { type Meeting, type MeetingId } from './meetings.types';

const getMeetingId = (location: {
  pathname: string;
}): MeetingId | undefined => {
  const match = location.pathname.match(/^\/meetings\/([^/]+)$/);
  return match ? match[1] : undefined;
};

const getMeeting = (meetingId?: MeetingId): Meeting | undefined => {
  return meetingId ? getMeetingById(meetingId) : undefined;
};

const meetingSection = ({ location }: CommandBarContext) =>
  getMeeting(getMeetingId(location))?.name ?? 'Meeting';

const meetingVisible = ({ location }: CommandBarContext) =>
  !!getMeetingId(location);

export const shareMeetingNote = createAction({
  id: 'share-meeting-note',
  name: 'Share…',
  keywords: 'share meeting note',
  section: meetingSection,
  visible: (context) => !userStore.hasSignedBaa && meetingVisible(context),
  icon: <Icons.share size={14} />,
  perform: ({ location }) => {
    const meetingId = getMeetingId(location);
    if (!meetingId) return;
    ModalsManager.show(ShareMeetingModal, { meetingId });
  },
  priority: 3,
});

export const copyMeetingId = createAction({
  id: 'copy-meeting-id',
  name: 'ID',
  keywords: 'copy meeting id',
  section: meetingSection,
  visible: meetingVisible,
  icon: <Icons.copy size={14} />,
  perform: ({ location }) => {
    const meetingId = getMeetingId(location);
    if (!meetingId) return;
    navigator.clipboard.writeText(meetingId);
    toast.success('Meeting ID copied');
  },
  priority: Priority.NORMAL,
});

export const copyMeetingLink = createAction({
  id: 'copy-meeting-link',
  name: 'Link',
  keywords: 'copy meeting link',
  section: meetingSection,
  visible: meetingVisible,
  icon: <Icons.copy size={14} />,
  perform: ({ location }) => {
    const meetingId = getMeetingId(location);
    if (!meetingId) return;
    const baseUrl =
      getEnvVar('BASE_URL_FOR_PUBLIC_LINKS') ?? window.location.origin;
    const meetingLink = new URL(`/meetings/${meetingId}`, baseUrl).toString();
    navigator.clipboard.writeText(meetingLink);
    toast.success('Meeting link copied');
  },
  priority: Priority.NORMAL,
});

export const copyMeetingTranscript = createAction({
  id: 'copy-meeting-transcript',
  name: 'Transcript',
  keywords: 'copy transcript',
  section: meetingSection,
  visible: ({ location }) => {
    const meetingId = getMeetingId(location);
    if (!meetingId) return false;
    // Same result as `getTranscriptLines().length > 0`, but it does not make the lines
    return (
      meetingStore.getCommittedTranscript(meetingId).trim() !== '' ||
      meetingStore.getInterimChunks(meetingId).length > 0
    );
  },
  icon: <Icons.copy size={14} />,
  perform: ({ location }) => {
    const meetingId = getMeetingId(location);
    if (!meetingId) return;
    const transcript = meetingStore.getTranscriptLines(meetingId).join('\n');
    if (!transcript) return;
    navigator.clipboard.writeText(transcript);
    toast.success('Transcript copied');
  },
  priority: Priority.HIGH,
});

export const copyMeetingSummary = createAction({
  id: 'copy-meeting-summary',
  name: 'Summary',
  keywords: 'copy summary',
  section: meetingSection,
  visible: ({ location }) => {
    const meeting = getMeeting(getMeetingId(location));
    return !!meeting?.summary;
  },
  icon: <Icons.copy size={14} />,
  perform: ({ location }) => {
    const meeting = getMeeting(getMeetingId(location));
    if (!meeting?.summary) return;
    navigator.clipboard.writeText(meeting.summary);
    toast.success('Summary copied');
  },
  priority: Priority.HIGH,
});

export const copyMeetingNotes = createAction({
  id: 'copy-meeting-notes',
  name: 'Note',
  keywords: 'copy note',
  section: meetingSection,
  visible: ({ location }) => {
    const meeting = getMeeting(getMeetingId(location));
    return !!meeting?.notes;
  },
  icon: <Icons.copy size={14} />,
  perform: ({ location }) => {
    const meeting = getMeeting(getMeetingId(location));
    if (!meeting?.notes) return;
    const parsed = JSONContentSchema.safeParse(meeting.notes);
    const text = getText(parsed.success ? parsed.data : EMPTY_JSON_CONTENT);
    navigator.clipboard.writeText(text);
    toast.success('Notes copied');
  },
  priority: Priority.HIGH,
});

export const startNewMeeting = createAction({
  id: 'start-new-meeting',
  name: 'New Meeting Note',
  section: 'Actions',
  visible: ({ location }) => {
    if (!isCapable(AppCapability.RecordMeeting)) return false;
    const onMeetingDetail = !!getMeetingId(location);
    return !onMeetingDetail && meetingStore.recording.status === 'idle';
  },
  icon: <Icons.filePlus size={14} />,
  perform: async () => {
    const toastId = toast.loading('New Meeting Note', {
      description: 'Starting...',
    });
    try {
      await meetingApis.enqueueStartMeeting({
        payload: { startReason: 'manual' },
      });
      toast.success('New Meeting Note', {
        id: toastId,
        description: 'Transcription started',
      });
    } catch (_error) {
      toast.error('New Meeting Note', {
        id: toastId,
        description: 'Failed to start meeting',
      });
    }
  },
  priority: Priority.HIGH,
});

export const stopMeetingRecording = createAction({
  id: 'stop-meeting-recording',
  name: 'Stop Transcription',
  section: 'Actions',
  visible: () => meetingStore.recording.status === 'recording',
  icon: <Icons.stopAlt size={14} />,
  perform: async () => {
    await meetingApis
      .enqueueStopMeeting({ type: 'stop_note_taking' }, { deferSummary: true })
      .catch(() => toast.error('Failed to stop transcription'));
  },
  priority: Priority.HIGH,
});

export const deleteMeetingNote = createAction({
  id: 'delete-meeting-note',
  name: 'Delete…',
  keywords: 'delete meeting note',
  section: meetingSection,
  visible: (context) =>
    meetingStore.recording.status !== 'recording' && meetingVisible(context),
  icon: <Icons.trash size={14} />,
  dangerous: true,
  children: [
    {
      section: Section.Settings,
      id: 'delete-meeting-note-confirm',
      name: 'Yes, delete meeting note',
      icon: <Icons.check size={14} />,
      visible: ({ query }) => !query.length,
      perform: async ({ location }) => {
        const meetingId = getMeetingId(location);
        if (!meetingId) return;
        const toastId = toast.loading('Delete Meeting Note', {
          description: 'Deleting...',
        });
        try {
          await meetingApis.deleteMeetingNotes([meetingId]);
          toast.success('Delete Meeting Note', {
            id: toastId,
            description: 'Meeting note deleted',
          });
          navigate({ to: '/meetings' });
        } catch (_error) {
          toast.error('Delete Meeting Note', {
            id: toastId,
            description: 'Failed to delete meeting note',
          });
        }
      },
    },
    {
      section: Section.Settings,
      id: 'delete-meeting-note-cancel',
      name: 'Cancel',
      icon: <Icons.cancel size={14} />,
      visible: ({ query }) => !query.length,
      perform: () => {},
    },
  ],
  priority: 1,
});

const copyMeetingChildren = [
  copyMeetingLink,
  copyMeetingId,
  copyMeetingTranscript,
  copyMeetingSummary,
  copyMeetingNotes,
];

export const copyMeeting = createAction({
  id: 'copy-meeting',
  name: 'Copy…',
  keywords: 'copy meeting',
  section: meetingSection,
  visible: (context) =>
    meetingVisible(context) &&
    copyMeetingChildren.some((child) => child.visible?.(context) ?? true),
  icon: <Icons.copy size={14} />,
  children: copyMeetingChildren,
  priority: 2,
});

export const meetingActions = [
  startNewMeeting,
  stopMeetingRecording,
  ...[shareMeetingNote, copyMeeting, deleteMeetingNote].map((action) => ({
    ...action,
    contextKind: 'Meeting note' as const,
  })),
];
